import { CircularProgress, makeStyles } from '@material-ui/core';
import MuiAnalyticsIcon from '@material-ui/icons/InsertChart';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import {
  AnalyticsItemType,
  GetAnalyticsSummaryQuery,
  TimeOrder,
  useGetAnalyticsSummaryQuery,
} from '../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../theme';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { DialogTopBar } from '../dialog/DialogTopBar';
import { FetchMoreCallback, useInfiniteScroll } from '../feed/useInfiniteScroll';
import { AnalyticsLocation, useLogAnalytics } from '../shared/logAnalytics';
import { AnalyticsDetailed } from './AnalyticsDetailed';
import { AnalyticsSummaryView } from './AnalyticsSummaryView';

interface Props {
  plexId: string;
}

export const AnalyticsView = ({ plexId }: Props): React.ReactElement | null => {
  useLogAnalytics({ name: 'loaded page', details: { location: AnalyticsLocation.ANALYTICS } });
  const classes = useStyles();
  const [itemTypeFilter, setItemTypeFilter] = useState(AnalyticsItemType.Post);
  const [sortFilter, setSortFilter] = useState(TimeOrder.Desc);
  const variables = useMemo(
    () => ({
      plexId,
      targetType: itemTypeFilter,
      cursor: null,
      timeOrder: sortFilter,
    }),
    [itemTypeFilter, plexId, sortFilter]
  );
  const { data, loading, fetchMore } = useGetAnalyticsSummaryQuery({ variables });
  const nextCursor = data?.loggedIn?.plexById?.analytics.items.posts.nextCursor;

  const onFetchMoreItems: FetchMoreCallback = useCallback(
    (isCancelled) => {
      return fetchMore({
        variables: { ...variables, cursor: nextCursor },
        updateQuery: (prev: GetAnalyticsSummaryQuery, { fetchMoreResult }) => {
          if (
            !prev?.loggedIn?.plexById?.analytics ||
            !fetchMoreResult ||
            !fetchMoreResult?.loggedIn?.plexById?.analytics.items.posts.items.length ||
            isCancelled()
          ) {
            return prev;
          }
          return {
            ...prev,
            loggedIn: {
              ...prev.loggedIn,
              plexById: {
                ...prev.loggedIn.plexById,
                analytics: {
                  ...prev.loggedIn.plexById.analytics,
                  items: {
                    ...prev.loggedIn.plexById.analytics.items,
                    posts: {
                      ...prev.loggedIn.plexById.analytics.items.posts,
                      items: [
                        ...prev.loggedIn.plexById.analytics.items.posts.items,
                        ...fetchMoreResult.loggedIn.plexById.analytics.items.posts.items,
                      ],
                      nextCursor: fetchMoreResult.loggedIn.plexById.analytics.items.posts.nextCursor,
                    },
                    questions: {
                      ...prev.loggedIn.plexById.analytics.items.questions,
                      items: [
                        ...prev.loggedIn.plexById.analytics.items.questions.items,
                        ...fetchMoreResult.loggedIn.plexById.analytics.items.questions.items,
                      ],
                      nextCursor: fetchMoreResult.loggedIn.plexById.analytics.items.questions.nextCursor,
                    },
                  },
                },
              },
            },
          };
        },
      });
    },
    [fetchMore, nextCursor, variables]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const infiniteScrollLoading = useInfiniteScroll({
    containerRef: scrollRef,
    onFetchMore: onFetchMoreItems,
    canFetchMore: !loading && nextCursor != null,
  });

  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexTypography className={classes.rootTitle} size={TextSizesEnum.XXLarge}>
          <MuiAnalyticsIcon className={classes.rootTitleIcon} />
          {data?.loggedIn?.plexById?.name} Analytics
        </PlexTypography>
      </DialogTopBar>
      <AnalyticsSummaryView data={data?.loggedIn?.plexById?.analytics} />
      <AnalyticsDetailed
        data={data?.loggedIn?.plexById?.analytics?.items}
        itemTypeFilter={itemTypeFilter}
        setItemTypeFilter={setItemTypeFilter}
        sortFilter={sortFilter}
        setSortFilter={setSortFilter}
        scrollRef={scrollRef}
      />
      {(loading || infiniteScrollLoading) && <CircularProgress size={24} />}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    overflow: 'hidden',
    height: '100%',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
  },
  rootTitle: {
    fontWeight: 'bold',
    marginLeft: spacings.xlarge,
    marginTop: spacings.xlarge,
    marginBottom: spacings.xlarge,
    color: colorPalette.dark._800,
  },
  rootTitleIcon: {
    width: 32,
    height: 32,
    marginRight: spacings.normal,
  },
}));
